import React, { useState, useEffect, useRef } from "react";
import {
  BarChart2,
  Building,
  Users,
  AlertTriangle,
  Mail,
  Phone,
  Calendar,
  CheckSquare,
  Target,
  Briefcase,
  AlertCircle,
  Sparkles,
  TrendingUp,
  AlertOctagon,
  Building2,
} from "lucide-react";
import ChatComponent from "../common/ChatComponent";
import { hubSpotService } from "../../services/HubSpotService";
import { HubSpotData } from "../../services/HubSpotService";
import AIAnalysisLoader from "./AnalysisLoader";
import { AppConfig } from "../../utils/Constants";
import { useToastContext } from "../../context/ToastContext";
import { useNavigate } from "react-router-dom";
const suggestedPrompts = [
  { id: "1", text: "What are my main pipeline bottlenecks?" },
  { id: "2", text: "Analyze my deal conversion rates" },
  { id: "3", text: "Show me deals at risk of stalling" },
  { id: "4", text: "How can I improve my pipeline health?" },
  { id: "5", text: "Summarize my contact engagement" },
];

const handleSendMessage = async (message: string) => {
  await new Promise((resolve) => setTimeout(resolve, 1000));
  console.log("Sending message:", message);
};

const HubSpotAIAgentPage = () => {
  const [data, setHubspotData] = useState<HubSpotData | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { showToast } = useToastContext();
  const navigate = useNavigate();
  const hasFetchedRef = useRef(false);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    // Prevent multiple simultaneous fetches
    if (hasFetchedRef.current || data !== null || isFetchingRef.current) {
      return;
    }

    const fetchData = async () => {
      if (isFetchingRef.current) return; // Double check
      isFetchingRef.current = true;
      hasFetchedRef.current = true;
      setIsAnalyzing(true);
      try {
        const response = await hubSpotService.fetchHubSpotData();
        setHubspotData(response.data);
      } catch (error: any) {
        console.error("Error fetching HubSpot data:", error);
        showToast("error", `${error.message}`);
        hasFetchedRef.current = false; // Reset on error so user can retry
      } finally {
        setIsAnalyzing(false);
        isFetchingRef.current = false;
      }
    };

    fetchData();
  }, [data, showToast]);

  if (!data) {
    return <AIAnalysisLoader />;
  }

  return (
    <div className="flex flex-col w-full min-h-screen bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark relative overflow-hidden">
      {/* Subtle grid pattern */}
      <div className="subtle-grid"></div>
      
      {/* Magical gradient orbs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/5 dark:bg-secondary/10 rounded-full blur-3xl pointer-events-none animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-secondary/5 dark:bg-secondary/10 rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDelay: '1s' }}></div>
      
      <main className="flex-1 px-4 md:px-6 py-6 max-w-7xl mx-auto w-full relative z-10">
        {/* Compact header with live status */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-ping absolute"></div>
              <div className="w-2 h-2 bg-green-500 rounded-full relative"></div>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-black dark:text-text-primaryDark">
                HubSpot Intelligence
              </h1>
              <p className="text-xs text-black/50 dark:text-text-secondaryDark">
                Live data • AI-powered insights
              </p>
            </div>
          </div>
        </div>
        
        {/* Floating metrics - more dynamic and alive */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="group relative bg-background-paper dark:bg-background-paperDark rounded-xl p-4 border border-black/8 dark:border-divider-dark/50 shadow-sm hover:shadow-md hover:border-secondary/20 dark:hover:border-secondary/30 transition-all duration-300 hover:-translate-y-0.5">
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/5 dark:from-secondary/0 dark:to-secondary/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-secondary/10 dark:bg-secondary/20 rounded-lg">
                  <Briefcase className="w-3.5 h-3.5 text-secondary" />
                </div>
                <span className="text-xs font-medium text-black/60 dark:text-text-secondaryDark">
                  Deals
                </span>
              </div>
              <div className="text-2xl font-bold text-black dark:text-text-primaryDark mb-0.5">
                {data.insights.deals.total}
              </div>
              <div className="text-xs text-black/50 dark:text-text-secondaryDark/70">
                {data.insights.deals.active} active
              </div>
            </div>
          </div>
          
          <div className="group relative bg-background-paper dark:bg-background-paperDark rounded-xl p-4 border border-black/8 dark:border-divider-dark/50 shadow-sm hover:shadow-md hover:border-secondary/20 dark:hover:border-secondary/30 transition-all duration-300 hover:-translate-y-0.5">
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/5 dark:from-secondary/0 dark:to-secondary/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-secondary/10 dark:bg-secondary/20 rounded-lg">
                  <Building className="w-3.5 h-3.5 text-secondary" />
                </div>
                <span className="text-xs font-medium text-black/60 dark:text-text-secondaryDark">
                  Companies
                </span>
              </div>
              <div className="text-2xl font-bold text-black dark:text-text-primaryDark mb-0.5">
                {data.insights.companies.total}
              </div>
              <div className="text-xs text-black/50 dark:text-text-secondaryDark/70">
                {data.insights.companies.active} active
              </div>
            </div>
          </div>
          
          <div className="group relative bg-background-paper dark:bg-background-paperDark rounded-xl p-4 border border-black/8 dark:border-divider-dark/50 shadow-sm hover:shadow-md hover:border-secondary/20 dark:hover:border-secondary/30 transition-all duration-300 hover:-translate-y-0.5">
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/5 dark:from-secondary/0 dark:to-secondary/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-secondary/10 dark:bg-secondary/20 rounded-lg">
                  <Users className="w-3.5 h-3.5 text-secondary" />
                </div>
                <span className="text-xs font-medium text-black/60 dark:text-text-secondaryDark">
                  Contacts
                </span>
              </div>
              <div className="text-2xl font-bold text-black dark:text-text-primaryDark mb-0.5">
                {data.insights.contacts.total}
              </div>
              <div className="text-xs text-black/50 dark:text-text-secondaryDark/70">
                {data.insights.contacts.qualified} qualified
              </div>
            </div>
          </div>
          
          <div className="group relative bg-background-paper dark:bg-background-paperDark rounded-xl p-4 border border-black/8 dark:border-divider-dark/50 shadow-sm hover:shadow-md hover:border-secondary/20 dark:hover:border-secondary/30 transition-all duration-300 hover:-translate-y-0.5">
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/5 dark:from-secondary/0 dark:to-secondary/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-secondary/10 dark:bg-secondary/20 rounded-lg">
                  <Target className="w-3.5 h-3.5 text-secondary" />
                </div>
                <span className="text-xs font-medium text-black/60 dark:text-text-secondaryDark">
                  Pipelines
                </span>
              </div>
              <div className="text-2xl font-bold text-black dark:text-text-primaryDark mb-0.5">
                {data.insights.pipelines.total}
              </div>
              <div className="text-xs text-black/50 dark:text-text-secondaryDark/70">
                {data.insights.pipelines.healthyStages} healthy
              </div>
            </div>
          </div>
        </div>

        {/* Immersive chat interface - the star of the show */}
        <section className="relative bg-background-paper dark:bg-background-paperDark rounded-2xl border border-black/10 dark:border-divider-dark overflow-hidden shadow-lg">
          {/* Subtle glow effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 via-transparent to-secondary/5 dark:from-secondary/10 dark:via-transparent dark:to-secondary/10 pointer-events-none"></div>
          
          {/* Chat header - minimal and elegant */}
          <div className="relative p-5 border-b border-black/5 dark:border-divider-dark/50">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-8 h-8 bg-gradient-to-br from-secondary to-secondary-hover rounded-lg flex items-center justify-center shadow-sm">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-background-paper dark:border-background-paperDark animate-pulse"></div>
              </div>
              <div>
                <h2 className="text-base font-semibold text-black dark:text-text-primaryDark">
                  AI Assistant
                </h2>
                <p className="text-xs text-black/50 dark:text-text-secondaryDark">
                  Ready to analyze your data
                </p>
              </div>
            </div>
          </div>
          
          {/* Chat component - full immersive experience */}
          <div className="relative">
            <ChatComponent
              onSendMessage={handleSendMessage}
              suggestedPrompts={suggestedPrompts}
              className="h-[550px] md:h-[650px]"
              initialMessage={
                data.aiSummary ||
                `I'm your AI assistant, connected to your HubSpot instance. I can analyze your deals, pipelines, contacts, and companies in real-time. What would you like to explore?`
              }
              scrollOnMount={false}
            />
          </div>
        </section>
      </main>
      {isAnalyzing && <AIAnalysisLoader />}
    </div>
  );
};

export default HubSpotAIAgentPage;

